/**
 * PII Sanitizer for telemetry payloads
 * Story: 116-123 - Instrument API and error telemetry
 *
 * Enforces telemetry PII controls via explicit allowlist + sanitizer pattern.
 * Only properties in PII_ALLOWLIST are permitted in telemetry payloads.
 * All other fields are stripped before dispatch to App Insights.
 */

/**
 * Explicit allowlist of telemetry property keys that are safe to send.
 * Any property not in this set will be stripped from all telemetry payloads.
 *
 * Governed under OI-116-05: PII-scrubbing strategy for App Insights telemetry.
 */
export const PII_ALLOWLIST: ReadonlySet<string> = new Set([
  // Event metadata
  'event_name',
  'timestamp',

  // Product context
  'product',

  // Page context
  'page_path',
  'page_title',
  'page_type',

  // API telemetry fields
  'method',
  'status_code',
  'url_pattern',
  'duration_ms',
  'success',
  'correlation_id',
  'dependency_type',
  'dependency_name',

  // Error / exception telemetry
  'error_type',
  'error_code',
  'severity',
  'component',

  // CTA / Pricing events
  'cta_text',
  'cta_url',
  'link_type',
  'location',
  'button_text',
  'destination',
  'metrics_count',
  'has_substantiation',
  'takeaways_count',
  'has_cta',

  // Blog listing / article events
  'article_slug',
  'article_title',
  'article_count',
  'article_position',
  'article_path',
  'categories',
  'reading_time_minutes',
  'current_page',
  'from_page',
  'to_page',
  'total_pages',

  // Blog search events
  'result_count',
  'active_filters',
  'response_time_ms',
  'result_position',
  'clicked_article_id',

  // Blog engagement events
  'threshold_percent',
  'threshold_seconds',

  // Bookings events
  'status',
  'reason',
])

/**
 * Recursively sanitize a single value.
 * - Primitives (string, number, boolean, null, undefined) pass through unchanged.
 * - Arrays: each element is recursively sanitized.
 * - Objects: keys are filtered against PII_ALLOWLIST; values are recursively sanitized.
 */
function sanitizeValue(value: unknown): unknown {
  if (value === null || value === undefined) return value
  if (typeof value !== 'object') return value // primitives pass through
  if (Array.isArray(value)) {
    return value.map(sanitizeValue)
  }
  // Nested object: apply allowlist to keys, then recursively sanitize values
  return sanitizeTelemetryProperties(value as Record<string, unknown>)
}

/**
 * Sanitize a telemetry properties object.
 * Removes any keys not in the PII_ALLOWLIST.
 * Nested objects and arrays are recursively sanitized so that PII embedded at
 * any depth (e.g. `{ active_filters: { email: '…' } }`) is also stripped.
 *
 * @param properties - Raw properties object from a telemetry event
 * @returns Sanitized properties with only allowlisted keys retained at every level
 */
export function sanitizeTelemetryProperties(
  properties: Record<string, unknown>
): Record<string, unknown> {
  const sanitized: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(properties)) {
    if (PII_ALLOWLIST.has(key)) {
      sanitized[key] = sanitizeValue(value)
    }
  }
  return sanitized
}

/**
 * Count the number of properties dropped during sanitization.
 * Used for drop-counter evidence in synthetic tests.
 *
 * @param properties - Raw properties object
 * @returns Number of keys that would be dropped by sanitization
 */
export function countDroppedProperties(
  properties: Record<string, unknown>
): number {
  return Object.keys(properties).filter((key) => !PII_ALLOWLIST.has(key)).length
}
